import React, { useState } from "react";
import { Popover, Button, TextField, BlockStack, InlineStack, Text } from "@shopify/polaris";
import { CalendarIcon } from "@shopify/polaris-icons";
import moment from "moment";

// Shopify-style date range picker. Single button that opens a popover with
// preset shortcuts on the left and a custom from/to selector on the right.
//
// Presets are computed from `today` so reports that "look back" always show
// the same range when re-opened on a different day.

const PRESETS = [
  { id: "today", label: "Hoy", make: () => ({ from: today(), to: today() }) },
  { id: "yesterday", label: "Ayer", make: () => ({ from: shift(-1), to: shift(-1) }) },
  { id: "last7", label: "Últimos 7 días", make: () => ({ from: shift(-6), to: today() }) },
  { id: "last30", label: "Últimos 30 días", make: () => ({ from: shift(-29), to: today() }) },
  { id: "this_week", label: "Esta semana", make: () => weekRange(0) },
  { id: "last_week", label: "Semana pasada", make: () => weekRange(-1) },
  { id: "this_month", label: "Este mes", make: () => monthRange(0) },
  { id: "last_month", label: "Mes pasado", make: () => monthRange(-1) },
  { id: "this_year", label: "Este año", make: () => yearRange(0) },
  { id: "last_year", label: "Año pasado", make: () => yearRange(-1) },
];

function today() {
  return moment().format("YYYY-MM-DD");
}
function shift(days) {
  return moment().add(days, "days").format("YYYY-MM-DD");
}
function weekRange(offset) {
  const start = moment().isoWeekday(1).add(offset, "weeks");
  const end = start.clone().add(6, "days");
  return { from: start.format("YYYY-MM-DD"), to: end.format("YYYY-MM-DD") };
}
function monthRange(offset) {
  const m = moment().add(offset, "months");
  return {
    from: m.clone().startOf("month").format("YYYY-MM-DD"),
    to: m.clone().endOf("month").format("YYYY-MM-DD"),
  };
}
function yearRange(offset) {
  const m = moment().add(offset, "years");
  return {
    from: m.clone().startOf("year").format("YYYY-MM-DD"),
    to: m.clone().endOf("year").format("YYYY-MM-DD"),
  };
}

function fmtSpan(from, to) {
  if (!from || !to) return "Seleccionar periodo";
  if (from === to) return moment(from).format("DD MMM YYYY");
  return `${moment(from).format("DD MMM YYYY")} – ${moment(to).format("DD MMM YYYY")}`;
}

function DateRangeFilter({ label, from, to, onChange }) {
  const [open, setOpen] = useState(false);
  const [draftFrom, setDraftFrom] = useState(from || "");
  const [draftTo, setDraftTo] = useState(to || "");

  const activator = (
    <Button icon={CalendarIcon} onClick={() => {
      setDraftFrom(from || "");
      setDraftTo(to || "");
      setOpen((o) => !o);
    }}>
      {fmtSpan(from, to)}
    </Button>
  );

  const applyPreset = (preset) => {
    const { from: f, to: t } = preset.make();
    onChange({ from: f, to: t });
    setOpen(false);
  };

  const applyCustom = () => {
    if (!draftFrom || !draftTo) return;
    onChange({ from: draftFrom, to: draftTo });
    setOpen(false);
  };

  return (
    <BlockStack gap="100">
      {label && <Text variant="bodySm">{label}</Text>}
      <Popover active={open} activator={activator} onClose={() => setOpen(false)} preferredAlignment="left">
        <div style={{ display: "flex", padding: 12, gap: 12, minWidth: 460 }}>
          <div style={{ width: 180, borderRight: "1px solid var(--p-color-border-secondary)", paddingRight: 8 }}>
            <BlockStack gap="050">
              {PRESETS.map((p) => (
                <Button key={p.id} variant="tertiary" textAlign="start" onClick={() => applyPreset(p)} fullWidth>
                  {p.label}
                </Button>
              ))}
            </BlockStack>
          </div>
          <div style={{ flex: 1 }}>
            <BlockStack gap="200">
              <TextField
                label="Desde"
                type="date"
                value={draftFrom}
                onChange={setDraftFrom}
                autoComplete="off"
              />
              <TextField
                label="Hasta"
                type="date"
                value={draftTo}
                onChange={setDraftTo}
                autoComplete="off"
              />
              <InlineStack align="end" gap="200">
                <Button onClick={() => setOpen(false)}>Cancelar</Button>
                <Button variant="primary" onClick={applyCustom} disabled={!draftFrom || !draftTo}>
                  Aplicar
                </Button>
              </InlineStack>
            </BlockStack>
          </div>
        </div>
      </Popover>
    </BlockStack>
  );
}

export default DateRangeFilter;
